import { useCallback, useEffect, useState } from "react";
import Button from "../../components/common/Button";
import {
  FiClock,
  FiSearch,
  FiFilter,
  FiChevronDown,
  FiChevronRight,
  FiAlertCircle,
  FiCalendar,
  FiRefreshCw
} from "react-icons/fi";
import reportService from "../../services/reportService";
import customerService from "../../services/customerService";
import alertService from "../../utils/alertService";
import SearchableDropdown from "../../components/common/SearchableDropdown";

function formatCurrency(value) {
  const num = Number(value || 0);
  return `₹ ${num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function downloadBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export default function ARAgingReportPage() {
  const [summary, setSummary] = useState([]);
  const [grandTotal, setGrandTotal] = useState({
    current: 0,
    days1_30: 0,
    days31_60: 0,
    days61_90: 0,
    days91_over: 0,
    totalRemainingAmount: 0
  });
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [expandedCustomers, setExpandedCustomers] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [downloadingFormat, setDownloadingFormat] = useState("");
  const [error, setError] = useState("");

  // Load active customers list for filter dropdown
  useEffect(() => {
    async function loadCustomers() {
      try {
        const customerList = await customerService.getActiveCustomers();
        const items = Array.isArray(customerList)
          ? customerList
          : customerList?.items || customerList?.data || [];
        setCustomers(items);
      } catch (err) {
        console.error("Failed to load customers:", err);
      }
    }
    loadCustomers();
  }, []);

  // Fetch AR Aging Report Data
  const fetchReport = useCallback(async () => {
    setIsLoading(true);
    setError("");
    try {
      const params = {};
      if (selectedCustomerId) params.customerId = selectedCustomerId;
      if (searchTerm) params.search = searchTerm;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const data = await reportService.getARAgingReport(params);
      setSummary(data.summary || []);
      setGrandTotal(
        data.grandTotal || {
          current: 0,
          days1_30: 0,
          days31_60: 0,
          days61_90: 0,
          days91_over: 0,
          totalRemainingAmount: 0
        }
      );
    } catch (err) {
      console.error("Error fetching AR aging report:", err);
      setError(err?.message || "Failed to load AR Aging Report.");
    } finally {
      setIsLoading(false);
    }
  }, [selectedCustomerId, searchTerm, startDate, endDate]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const toggleCustomerExpand = (customerKey) => {
    setExpandedCustomers((prev) => ({
      ...prev,
      [customerKey]: !prev[customerKey]
    }));
  };

  const handleDownload = async (format) => {
    setDownloadingFormat(format);
    try {
      const params = {};
      if (selectedCustomerId) params.customerId = selectedCustomerId;
      if (searchTerm) params.search = searchTerm;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const blob = await reportService.downloadARAgingReport(format, params);
      const ext = format === "excel" || format === "xlsx" ? "xlsx" : format;
      downloadBlob(blob, `ar-aging-report.${ext}`);
      alertService.success(`Report downloaded successfully (${format.toUpperCase()})`);
    } catch (err) {
      alertService.error(err?.message || `Failed to download ${format.toUpperCase()} report.`);
    } finally {
      setDownloadingFormat("");
    }
  };

  const handleResetFilters = () => {
    setSelectedCustomerId("");
    setSearchTerm("");
    setStartDate("");
    setEndDate("");
  };

  const isFiltered = Boolean(selectedCustomerId || searchTerm || startDate || endDate);

  return (
    <div className="min-h-screen bg-slate-50 p-3 md:p-4 space-y-4">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 bg-white p-4 rounded-xl shadow-sm border border-slate-200/80">
        <div className="space-y-0.5">
          <div className="flex items-center gap-2">
            <span className="p-1.5 bg-emerald-50 text-emerald-600 rounded-lg">
              <FiClock className="w-4 h-4" />
            </span>
            <h1 className="text-lg font-bold text-slate-800 tracking-tight">
              Accounts Receivable Aging
            </h1>
          </div>
          <p className="text-xs text-slate-500 pl-8">
            Track outstanding customer invoice balances grouped customer-wise across aging periods.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-2 sm:flex sm:w-auto sm:items-center">
          <Button
            variant="secondary"
            size="sm"
            onClick={fetchReport}
            isLoading={isLoading}
            className="col-span-2 sm:col-auto"
          >
            <span className="flex items-center justify-center gap-1.5">
              <i className="material-icons text-sm">refresh</i>
              <span className="sm:inline">Refresh</span>
            </span>
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => handleDownload("xlsx")}
            isLoading={downloadingFormat === "xlsx"}
            disabled={Boolean(downloadingFormat)}
            className="flex-1 sm:flex-none"
          >
            <span className="flex items-center justify-center gap-1.5 text-green-600">
              <i className="material-icons text-sm">grid_on</i>
              <span className="text-[10px] sm:text-xs">EXCEL</span>
            </span>
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => handleDownload("pdf")}
            isLoading={downloadingFormat === "pdf"}
            disabled={Boolean(downloadingFormat)}
            className="flex-1 sm:flex-none"
          >
            <span className="flex items-center justify-center gap-1.5 text-red-600">
              <i className="material-icons text-sm">picture_as_pdf</i>
              <span className="text-[10px] sm:text-xs">PDF</span>
            </span>
          </Button>
        </div>
      </div>

      {/* KPI Cards Overview */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="bg-white p-3 rounded-xl border border-slate-200/80 shadow-sm space-y-0.5">
          <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">Current</span>
          <div className="text-base font-bold text-emerald-600">{formatCurrency(grandTotal.current)}</div>
          <span className="text-[10px] text-slate-400">Not overdue / Current</span>
        </div>

        <div className="bg-white p-3 rounded-xl border border-slate-200/80 shadow-sm space-y-0.5">
          <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">1-30 Days</span>
          <div className="text-base font-bold text-blue-600">{formatCurrency(grandTotal.days1_30)}</div>
          <span className="text-[10px] text-slate-400">1-30 days overdue</span>
        </div>

        <div className="bg-white p-3 rounded-xl border border-slate-200/80 shadow-sm space-y-0.5">
          <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">31-60 Days</span>
          <div className="text-base font-bold text-amber-600">{formatCurrency(grandTotal.days31_60)}</div>
          <span className="text-[10px] text-slate-400">31-60 days overdue</span>
        </div>

        <div className="bg-white p-3 rounded-xl border border-slate-200/80 shadow-sm space-y-0.5">
          <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">61-90 Days</span>
          <div className="text-base font-bold text-orange-600">{formatCurrency(grandTotal.days61_90)}</div>
          <span className="text-[10px] text-slate-400">61-90 days overdue</span>
        </div>

        <div className="bg-white p-3 rounded-xl border border-slate-200/80 shadow-sm space-y-0.5">
          <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">91+ Days</span>
          <div className="text-base font-bold text-rose-600">{formatCurrency(grandTotal.days91_over)}</div>
          <span className="text-[10px] text-slate-400">91+ days overdue</span>
        </div>

        <div className="bg-gradient-to-br from-slate-900 to-emerald-950 p-3 rounded-xl text-white shadow-md space-y-0.5">
          <span className="text-[11px] font-semibold text-emerald-200 uppercase tracking-wide">Total AR</span>
          <div className="text-base font-bold text-white">{formatCurrency(grandTotal.totalRemainingAmount)}</div>
          <span className="text-[10px] text-emerald-300">Total Outstanding</span>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="bg-white p-3 rounded-xl border border-slate-200/80 shadow-sm flex flex-col xl:flex-row items-stretch xl:items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2.5 flex-1">
          {/* Customer Filter */}
          <div className="min-w-[190px] w-full sm:w-auto">
            <SearchableDropdown
              name="customerId"
              value={selectedCustomerId}
              onChange={(e) => setSelectedCustomerId(e.target.value)}
              options={[
                { value: "", label: "All Customers" },
                ...customers.map((c) => ({
                  value: c.customerId || c.id,
                  label: c.customerName || c.companyName || c.name || "Unnamed Customer"
                }))
              ]}
              placeholder="All Customers"
              icon={FiFilter}
              buttonClassName="w-full px-2.5 py-1.5 bg-slate-50 border-slate-200 text-slate-700 text-xs rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all font-medium"
            />
          </div>

          {/* Search Term Filter */}
          <div className="relative min-w-[220px] w-full sm:w-auto">
            <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
            <input
              type="text"
              placeholder="Search customer or invoice no..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 bg-slate-50 border border-slate-200 text-slate-700 text-xs rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
            />
          </div>

          {/* Date Range Filters */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-36">
              <FiCalendar className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full pl-8 pr-2.5 py-1.5 bg-slate-50 border border-slate-200 text-slate-700 text-xs rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
              />
            </div>
            <span className="text-slate-400 text-xs font-medium">to</span>
            <div className="relative flex-1 sm:w-36">
              <FiCalendar className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full pl-8 pr-2.5 py-1.5 bg-slate-50 border border-slate-200 text-slate-700 text-xs rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
              />
            </div>
          </div>
        </div>

        {/* Reset Filter Button */}
        {isFiltered && (
          <button
            onClick={handleResetFilters}
            className="text-xs text-emerald-600 hover:text-emerald-800 font-medium hover:underline self-end xl:self-center"
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Error Alert */}
      {error && (
        <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 flex items-center gap-2">
          <FiAlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-xs font-medium">{error}</span>
        </div>
      )}

      {/* Main Data Table */}
      <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm overflow-hidden">
        <div className="overflow-x-auto max-h-[480px] overflow-y-auto custom-scrollbar">
          <table className="w-full text-left border-collapse">
            <thead className="sticky top-0 z-10 bg-slate-100 shadow-sm">
              <tr className="bg-slate-100 text-slate-700 text-[11px] font-bold uppercase tracking-wide border-b border-slate-200">
                <th className="py-2.5 px-3 bg-slate-100 sticky top-0">Customer Name</th>
                <th className="py-2.5 px-3 text-right bg-slate-100 sticky top-0">Current</th>
                <th className="py-2.5 px-3 text-right bg-slate-100 sticky top-0">1-30 Days</th>
                <th className="py-2.5 px-3 text-right bg-slate-100 sticky top-0">31-60 Days</th>
                <th className="py-2.5 px-3 text-right bg-slate-100 sticky top-0">61-90 Days</th>
                <th className="py-2.5 px-3 text-right bg-slate-100 sticky top-0">91 Days & Over</th>
                <th className="py-2.5 px-3 text-right bg-slate-100 sticky top-0">Total Remaining</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200/70 text-xs text-slate-700">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-slate-400">
                    <div className="inline-flex items-center gap-2">
                      <FiRefreshCw className="animate-spin w-4 h-4 text-emerald-600" />
                      <span>Loading AR Aging Report data...</span>
                    </div>
                  </td>
                </tr>
              ) : summary.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-slate-400">
                    No outstanding customer invoices found for the selected criteria.
                  </td>
                </tr>
              ) : (
                summary.map((row, idx) => {
                  const customerKey = row.customerId || `row-${idx}`;
                  const isExpanded = Boolean(expandedCustomers[customerKey]);
                  const invoices = row.invoices || row.bills || [];

                  return (
                    <tr key={customerKey} className="contents">
                      {/* Customer Group Row */}
                      <tr
                        onClick={() => toggleCustomerExpand(customerKey)}
                        className="hover:bg-slate-50/80 cursor-pointer transition-colors font-medium border-b border-slate-100"
                      >
                        <td className="py-2.5 px-3 font-semibold text-slate-800 flex items-center gap-2">
                          <span className="text-slate-400 hover:text-slate-600 transition-colors">
                            {isExpanded ? <FiChevronDown className="w-3.5 h-3.5" /> : <FiChevronRight className="w-3.5 h-3.5" />}
                          </span>
                          {row.customerName}
                          {invoices.length > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded-full font-normal">
                              {invoices.length} {invoices.length === 1 ? "invoice" : "invoices"}
                            </span>
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-right font-medium text-emerald-700">
                          {row.current > 0 ? formatCurrency(row.current) : "-"}
                        </td>
                        <td className="py-2.5 px-3 text-right font-medium text-blue-700">
                          {row.days1_30 > 0 ? formatCurrency(row.days1_30) : "-"}
                        </td>
                        <td className="py-2.5 px-3 text-right font-medium text-amber-700">
                          {row.days31_60 > 0 ? formatCurrency(row.days31_60) : "-"}
                        </td>
                        <td className="py-2.5 px-3 text-right font-medium text-orange-700">
                          {row.days61_90 > 0 ? formatCurrency(row.days61_90) : "-"}
                        </td>
                        <td className="py-2.5 px-3 text-right font-medium text-rose-700">
                          {row.days91_over > 0 ? formatCurrency(row.days91_over) : "-"}
                        </td>
                        <td className="py-2.5 px-3 text-right font-bold text-slate-900 bg-slate-50/50">
                          {formatCurrency(row.totalRemainingAmount)}
                        </td>
                      </tr>

                      {/* Expandable Invoice Details Table */}
                      {isExpanded && invoices.length > 0 && (
                        <tr className="bg-slate-50/50">
                          <td colSpan={7} className="p-3 pl-8 border-b border-slate-200">
                            <div className="bg-white rounded-lg p-2.5 border border-slate-200 shadow-inner overflow-hidden">
                              <h4 className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                                <FiCalendar className="w-3 h-3" />
                                Individual Invoices for {row.customerName}
                              </h4>
                              <div className="overflow-x-auto max-h-[320px] overflow-y-auto custom-scrollbar">
                                <table className="w-full text-[11px] text-left">
                                  <thead className="sticky top-0 z-10 bg-slate-100">
                                    <tr className="bg-slate-100 text-slate-600 font-semibold border-b border-slate-200">
                                      <th className="py-1.5 px-2.5 bg-slate-100 sticky top-0">Invoice No</th>
                                      <th className="py-1.5 px-2.5 bg-slate-100 sticky top-0">Invoice Date</th>
                                      <th className="py-1.5 px-2.5 bg-slate-100 sticky top-0">Due Date</th>
                                      <th className="py-1.5 px-2.5 text-right bg-slate-100 sticky top-0">Total Amount</th>
                                      <th className="py-1.5 px-2.5 text-right bg-slate-100 sticky top-0">Paid Amount</th>
                                      <th className="py-1.5 px-2.5 text-right bg-slate-100 sticky top-0">Remaining Balance</th>
                                      <th className="py-1.5 px-2.5 text-center bg-slate-100 sticky top-0">Aging Category</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-slate-100">
                                    {invoices.map((inv, invIdx) => (
                                      <tr key={inv.sellingMasterId || inv.invoiceNo || inv.invoiceNumber || `inv-${invIdx}`} className="hover:bg-slate-50">
                                        <td className="py-1.5 px-2.5 font-semibold text-slate-800">
                                          {inv.invoiceNo || inv.invoiceNumber || inv.billNo || "-"}
                                        </td>
                                        <td className="py-1.5 px-2.5 text-slate-600">
                                          {inv.date || inv.billDate ? new Date(inv.date || inv.billDate).toLocaleDateString() : "-"}
                                        </td>
                                        <td className="py-1.5 px-2.5 text-slate-600">
                                          {inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : "-"}
                                        </td>
                                        <td className="py-1.5 px-2.5 text-right text-slate-700">{formatCurrency(inv.totalAmount)}</td>
                                        <td className="py-1.5 px-2.5 text-right text-emerald-600">{formatCurrency(inv.paidAmount)}</td>
                                        <td className="py-1.5 px-2.5 text-right font-bold text-slate-900">
                                          {formatCurrency(inv.remainingAmount ?? inv.balanceAmount ?? 0)}
                                        </td>
                                        <td className="py-1.5 px-2.5 text-center">
                                          <span
                                            className={`inline-block px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide ${inv.bucket === "current"
                                              ? "bg-emerald-100 text-emerald-800"
                                              : inv.bucket === "days1_30"
                                                ? "bg-blue-100 text-blue-800"
                                                : inv.bucket === "days31_60"
                                                  ? "bg-amber-100 text-amber-800"
                                                  : inv.bucket === "days61_90"
                                                    ? "bg-orange-100 text-orange-800"
                                                    : "bg-rose-100 text-rose-800"
                                              }`}
                                          >
                                            {inv.bucketName || inv.bucket}
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>

            {/* Sticky Grand Total Footer */}
            {summary.length > 0 && (
              <tfoot className="sticky bottom-0 z-10 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
                <tr className="bg-slate-900 text-white font-bold text-xs border-t-2 border-slate-700">
                  <td className="py-3 px-3 uppercase tracking-wide bg-slate-900 sticky bottom-0">Grand Total</td>
                  <td className="py-3 px-3 text-right text-emerald-400 bg-slate-900 sticky bottom-0">{formatCurrency(grandTotal.current)}</td>
                  <td className="py-3 px-3 text-right text-blue-400 bg-slate-900 sticky bottom-0">{formatCurrency(grandTotal.days1_30)}</td>
                  <td className="py-3 px-3 text-right text-amber-400 bg-slate-900 sticky bottom-0">{formatCurrency(grandTotal.days31_60)}</td>
                  <td className="py-3 px-3 text-right text-orange-400 bg-slate-900 sticky bottom-0">{formatCurrency(grandTotal.days61_90)}</td>
                  <td className="py-3 px-3 text-right text-rose-400 bg-slate-900 sticky bottom-0">{formatCurrency(grandTotal.days91_over)}</td>
                  <td className="py-3 px-3 text-right text-white bg-slate-950 sticky bottom-0">{formatCurrency(grandTotal.totalRemainingAmount)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
